<template>
  <div v-if="visible" class="hrvec-chatbot text_google">
    <!-- ปุ่มเปิดแชท -->
    <transition name="hcb-fade">
      <button
        v-show="!open"
        type="button"
        class="hcb-fab"
        aria-label="เปิดผู้ช่วยตอบคำถาม HRVEC"
        @click="toggle(true)"
      >
        <v-icon color="white" size="28">mdi-chat-question-outline</v-icon>
        <span v-if="showHint" class="hcb-fab-hint">มีข้อสงสัย ถามได้เลย</span>
        <span v-if="!seen" class="hcb-fab-dot"></span>
      </button>
    </transition>

    <!-- หน้าต่างแชท -->
    <transition name="hcb-slide">
      <section
        v-show="open"
        class="hcb-panel"
        role="dialog"
        aria-label="ผู้ช่วยตอบคำถาม HRVEC"
        @keydown.esc="toggle(false)"
      >
        <header class="hcb-header">
          <v-avatar size="38" color="white" class="mr-3">
            <v-icon color="primary">mdi-robot-happy-outline</v-icon>
          </v-avatar>
          <div class="hcb-header-text">
            <div class="hcb-title">ผู้ช่วย HRVEC</div>
            <div class="hcb-subtitle">{{ roleLabel }}</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon small dark title="เริ่มใหม่" aria-label="เริ่มบทสนทนาใหม่" @click="reset">
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
          <v-btn icon small dark title="ปิด" aria-label="ปิดหน้าต่างแชท" @click="toggle(false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </header>

        <div ref="body" class="hcb-body" role="log" aria-live="polite">
          <div v-for="msg in messages" :key="msg.key" :class="['hcb-row', msg.from === 'user' ? 'is-user' : 'is-bot']">
            <!-- ข้อความผู้ใช้ -->
            <div v-if="msg.from === 'user'" class="hcb-bubble hcb-bubble-user">{{ msg.text }}</div>

            <!-- ข้อความบอท -->
            <div v-else class="hcb-bubble hcb-bubble-bot">
              <div v-if="msg.intentTitle" class="hcb-answer-title">{{ msg.intentTitle }}</div>
              <p v-if="msg.text" class="hcb-text">
                <template v-for="(seg, i) in richText(msg.text)">
                  <strong v-if="seg.bold" :key="i">{{ seg.text }}</strong>
                  <span v-else :key="i">{{ seg.text }}</span>
                </template>
              </p>

              <ol v-if="msg.steps && msg.steps.length" class="hcb-steps">
                <li v-for="(step, i) in msg.steps" :key="i">
                  <template v-for="(seg, j) in richText(step)">
                    <strong v-if="seg.bold" :key="j">{{ seg.text }}</strong>
                    <span v-else :key="j">{{ seg.text }}</span>
                  </template>
                </li>
              </ol>

              <div v-if="msg.tip" class="hcb-tip">
                <v-icon small color="amber darken-3" class="mr-1">mdi-lightbulb-on-outline</v-icon>
                <span>{{ msg.tip }}</span>
              </div>

              <div v-if="msg.roleNote" class="hcb-role-note">
                <v-icon small class="mr-1">mdi-account-key-outline</v-icon>{{ msg.roleNote }}
              </div>

              <div v-if="msg.links && msg.links.length" class="hcb-links">
                <v-btn
                  v-for="link in msg.links"
                  :key="link.to + link.label"
                  small
                  depressed
                  rounded
                  color="primary"
                  class="mr-1 mb-1 text-none"
                  @click="go(link.to)"
                >
                  <v-icon left x-small>mdi-arrow-right-circle</v-icon>{{ link.label }}
                </v-btn>
              </div>

              <div v-if="msg.feedback !== undefined" class="hcb-feedback">
                <template v-if="msg.feedback === null">
                  <span>คำตอบนี้ช่วยได้ไหม?</span>
                  <v-btn icon x-small aria-label="ช่วยได้" @click="rate(msg, 1)"><v-icon small>mdi-thumb-up-outline</v-icon></v-btn>
                  <v-btn icon x-small aria-label="ไม่ช่วย" @click="rate(msg, -1)"><v-icon small>mdi-thumb-down-outline</v-icon></v-btn>
                </template>
                <span v-else-if="msg.feedback === 1">ขอบคุณสำหรับความคิดเห็นครับ 🙏</span>
                <span v-else>ขออภัยครับ ลองเลือกหัวข้อใกล้เคียงด้านล่าง หรือติดต่อเจ้าหน้าที่</span>
              </div>
            </div>

            <!-- ปุ่มคำถามแนะนำใต้ข้อความ -->
            <div v-if="msg.chips && msg.chips.length" class="hcb-chips">
              <span v-if="msg.chipsLabel" class="hcb-chips-label">{{ msg.chipsLabel }}</span>
              <v-chip
                v-for="chip in msg.chips"
                :key="chip.id"
                small
                outlined
                color="primary"
                class="mr-1 mb-1"
                @click="askIntent(chip)"
              >
                {{ chip.title }}
              </v-chip>
            </div>

            <!-- หมวดหมู่ทั้งหมด -->
            <div v-if="msg.categories" class="hcb-categories">
              <button
                v-for="cat in categories"
                :key="cat.id"
                type="button"
                class="hcb-category"
                @click="showCategory(cat)"
              >
                <v-icon small color="primary">{{ cat.icon }}</v-icon>
                <span>{{ cat.label }}</span>
              </button>
            </div>
          </div>

          <div v-if="typing" class="hcb-row is-bot">
            <div class="hcb-bubble hcb-bubble-bot hcb-typing" aria-label="กำลังพิมพ์">
              <span></span><span></span><span></span>
            </div>
          </div>
        </div>

        <form class="hcb-input" @submit.prevent="send">
          <input
            ref="input"
            v-model.trim="draft"
            type="text"
            maxlength="200"
            placeholder="พิมพ์คำถาม เช่น ลืมรหัสผ่าน, ยื่นย้าย..."
            aria-label="พิมพ์คำถาม"
            autocomplete="off"
          />
          <v-btn icon color="primary" type="submit" :disabled="!draft" aria-label="ส่งคำถาม">
            <v-icon>mdi-send</v-icon>
          </v-btn>
        </form>
        <div class="hcb-footnote">
          คำตอบอัตโนมัติจากคู่มือระบบ · กรุณาอย่าพิมพ์เลขบัตรประชาชน/รหัสผ่านในช่องนี้
        </div>
      </section>
    </transition>
  </div>
</template>

<script>
/**
 * HrvecChatbot.vue — ผู้ช่วยตอบคำถามการใช้งานระบบ (widget ลอยมุมขวาล่าง แสดงทุกหน้าที่อยู่ใต้ layout หลัก)
 * ความรู้หลักอยู่ใน knowledgeBase.js + รายการที่ผู้ดูแลระบบเพิ่ม/แก้ไขที่ /admin/chatbot_manage (chatbot_faq.php)
 * ตัวจับคู่คำถามอยู่ใน chatEngine.js (ทำงานฝั่ง browser ไม่ต้องรอ API)
 * ส่ง log คำถาม (ปิดบังข้อมูลส่วนบุคคลแล้ว) ไปที่ chatbot_log.insert.php เพื่อใช้ปรับปรุงฐานความรู้ —
 * ถ้า API ล่ม/ยังไม่ได้รัน MIGRATION_chatbot_log.sql แชทยังใช้งานได้ปกติ แค่ไม่มี log
 */
import { CATEGORIES, ROLE_LABELS } from "./knowledgeBase";
import {
  containsPersonalData,
  detectRole,
  getIntent,
  intentsInCategory,
  linksFor,
  loadCustomIntents,
  maskPersonalData,
  matchQuestion,
  quickRepliesFor
} from "./chatEngine";

const SEEN_KEY = "hrvec_chatbot_seen";
const TYPING_DELAY_MS = 450;

// หน้าที่ไม่ต้องแสดงแชท: หน้าพิมพ์เอกสาร/ฟอร์ม standalone (route ชั้นเดียว ไม่อยู่ใต้ layout หลัก)
const HIDDEN_PATH_RE = /print|pdf|thaid_callback/i;

let keySeq = 0;
const nextKey = () => `m${++keySeq}`;

function safeStorage(fn, fallback) {
  try {
    return fn();
  } catch (e) {
    return fallback;
  }
}

export default {
  name: "HrvecChatbot",
  data() {
    return {
      open: false,
      draft: "",
      typing: false,
      messages: [],
      role: "guest",
      seen: safeStorage(() => localStorage.getItem(SEEN_KEY) === "1", true),
      showHint: false,
      categories: CATEGORIES
    };
  },
  computed: {
    visible() {
      const route = this.$route;
      return route.matched.length >= 2 && !HIDDEN_PATH_RE.test(route.path);
    },
    roleLabel() {
      return ROLE_LABELS[this.role] || ROLE_LABELS.guest;
    }
  },
  watch: {
    // แจ้งสถานะปุ่มแชทผ่าน class บน <body> เพื่อให้ปุ่มลอยอื่น (#settings-wrapper: เครื่องมือผู้ดูแลระบบ / back to top)
    // ขยับขึ้นไปเรียงเหนือปุ่มแชท แทนการซ้อนทับตำแหน่งเดียวกัน (ดู hrvec-theme.scss ส่วน FAB stack)
    visible: {
      immediate: true,
      handler(val) {
        document.body.classList.toggle("hv-chatbot-on", !!val);
      }
    },
    // login/logout เปลี่ยนหน้า → ตรวจกลุ่มผู้ใช้ใหม่ ถ้ากลุ่มเปลี่ยนให้เริ่มบทสนทนาใหม่ตามกลุ่มนั้น
    $route() {
      const role = detectRole();
      if (role !== this.role) {
        this.role = role;
        if (this.messages.length) this.reset();
      }
    }
  },
  created() {
    this.role = detectRole();
    // คำถาม-คำตอบที่ผู้ดูแลระบบเพิ่มเอง (/admin/chatbot_manage) — โหลดล่วงหน้าให้พร้อมก่อนผู้ใช้เปิดแชท
    loadCustomIntents(this.$http);
  },
  mounted() {
    if (!this.seen) {
      this.hintTimer = setTimeout(() => {
        this.showHint = true;
        this.hintTimer = setTimeout(() => {
          this.showHint = false;
        }, 8000);
      }, 2500);
    }
  },
  beforeDestroy() {
    document.body.classList.remove("hv-chatbot-on");
    clearTimeout(this.hintTimer);
    clearTimeout(this.typingTimer);
  },
  methods: {
    toggle(state) {
      this.open = state;
      this.showHint = false;
      if (!state) return;
      this.role = detectRole();
      if (!this.seen) {
        this.seen = true;
        safeStorage(() => localStorage.setItem(SEEN_KEY, "1"));
      }
      if (!this.messages.length) {
        // ปกติโหลดเสร็จตั้งแต่ created แล้ว — ถ้าครั้งก่อนล้มเหลวจะลองใหม่ให้อัตโนมัติ
        loadCustomIntents(this.$http).then(() => {
          if (!this.messages.length) this.greet();
        });
      }
      this.$nextTick(() => {
        if (this.$refs.input) this.$refs.input.focus();
        this.scrollToBottom();
      });
    },

    greet() {
      const intro =
        this.role === "guest"
          ? "สวัสดีครับ 🙏 ผมคือผู้ช่วยตอบคำถามระบบ HRVEC\nพิมพ์คำถามได้เลย หรือเลือกหัวข้อที่พบบ่อยด้านล่างครับ"
          : `สวัสดีครับ 🙏 ท่านเข้าใช้งานในฐานะ **${this.roleLabel}**\nพิมพ์คำถามได้เลย หรือเลือกหัวข้อที่พบบ่อยสำหรับท่านด้านล่างครับ`;
      this.pushBot({
        text: intro,
        chips: quickRepliesFor(this.role),
        chipsLabel: "คำถามที่พบบ่อย",
        categories: true
      });
    },

    reset() {
      clearTimeout(this.typingTimer);
      this.typing = false;
      this.messages = [];
      this.greet();
    },

    send() {
      const text = this.draft;
      if (!text || this.typing) return;
      this.draft = "";
      this.pushUser(text);

      if (containsPersonalData(text)) {
        this.reply({
          text:
            "⚠️ เพื่อความปลอดภัยของข้อมูลส่วนบุคคล **กรุณาอย่าพิมพ์เลขบัตรประชาชนหรืออีเมลในช่องแชท** ผมไม่สามารถตรวจสอบข้อมูลรายบุคคลได้\nลองพิมพ์เฉพาะเรื่องที่ต้องการสอบถาม เช่น \"ลืมรหัสผ่าน\" ครับ"
        });
        this.log(text, null, "pii");
        return;
      }

      const result = matchQuestion(text, this.role);
      if (result.intent) {
        const msg = this.answerMessage(result.intent);
        if (result.alternatives.length) {
          msg.chips = result.alternatives;
          msg.chipsLabel = result.confidence === "high" ? "เรื่องใกล้เคียง" : "หรือท่านหมายถึง";
        }
        this.reply(msg, text, result);
      } else {
        this.reply(this.fallbackMessage(result.alternatives), text, result);
      }
    },

    askIntent(intent) {
      if (this.typing) return;
      this.pushUser(intent.title);
      this.reply(this.answerMessage(intent), intent.title, { intent, confidence: "click", score: 0 });
    },

    showCategory(cat) {
      if (this.typing) return;
      this.pushUser(`หมวด: ${cat.label}`);
      this.reply({
        text: `หัวข้อในหมวด **${cat.label}** เลือกเรื่องที่ต้องการได้เลยครับ`,
        chips: intentsInCategory(cat.id, this.role)
      });
    },

    answerMessage(intent) {
      const links = linksFor(intent, this.role);
      const hadRoleLinks = (intent.links || []).some(l => l.roles);
      let roleNote = "";
      // ประเด็นนี้มีเมนูเฉพาะกลุ่ม แต่ผู้ถามไม่ได้อยู่ในกลุ่มนั้น → บอกให้ชัดว่าเมนูอยู่ในบัญชีไหน
      if (hadRoleLinks && intent.roles && !intent.roles.includes(this.role)) {
        const owners = intent.roles.map(r => ROLE_LABELS[r]).join(" / ");
        roleNote = `เมนูที่เกี่ยวข้องอยู่ในบัญชี: ${owners}`;
      }
      const related = (intent.related || []).map(getIntent).filter(Boolean);
      return {
        intentId: intent.id,
        intentTitle: ["greeting", "thanks"].includes(intent.id) ? "" : intent.title,
        text: intent.answer,
        steps: intent.steps,
        tip: intent.tip,
        links,
        roleNote,
        chips: related,
        chipsLabel: related.length ? "คำถามต่อเนื่อง" : "",
        categories: intent.id === "greeting",
        feedback: ["greeting", "thanks"].includes(intent.id) ? undefined : null
      };
    },

    fallbackMessage(suggestions) {
      const contact = getIntent("general_contact");
      const chips = suggestions.length ? suggestions : quickRepliesFor(this.role);
      return {
        text:
          "ขออภัยครับ ผมยังไม่เข้าใจคำถามนี้ 🙇\nลองพิมพ์ให้สั้นลงด้วยคำสำคัญ เช่น \"ลืมรหัสผ่าน\" \"ยื่นย้าย\" \"ThaiD\" หรือเลือกหัวข้อด้านล่าง",
        chips: contact ? [...chips.filter(c => c.id !== contact.id), contact] : chips,
        chipsLabel: suggestions.length ? "หัวข้อที่อาจเกี่ยวข้อง" : "คำถามที่พบบ่อย",
        categories: true
      };
    },

    reply(msg, question, result) {
      clearTimeout(this.typingTimer);
      this.typing = true;
      this.scrollToBottom();
      this.typingTimer = setTimeout(() => {
        this.typing = false;
        const pushed = this.pushBot(msg);
        if (question !== undefined && result) {
          this.log(question, result.intent ? result.intent.id : null, result.confidence, result.score, pushed);
        }
      }, TYPING_DELAY_MS);
    },

    pushUser(text) {
      this.messages.push({ key: nextKey(), from: "user", text });
      this.scrollToBottom();
    },

    pushBot(msg) {
      const full = { key: nextKey(), from: "bot", ...msg };
      this.messages.push(full);
      this.scrollToBottom();
      return full;
    },

    go(path) {
      if (this.$route.path !== path) {
        this.$router.push(path).catch(() => {});
      }
      // จอเล็ก panel เต็มจอ → ปิดให้เห็นหน้าที่พาไป
      if (window.innerWidth < 600) this.open = false;
    },

    rate(msg, value) {
      msg.feedback = value;
      if (value < 0) {
        const contact = getIntent("general_contact");
        const chips = msg.chips || [];
        if (contact && !chips.some(c => c.id === contact.id)) {
          this.$set(msg, "chips", [...chips, contact]);
        }
      }
      if (msg.logId && msg.logToken) {
        this.$http
          .post("chatbot_log.insert.php", { action: "feedback", id: msg.logId, token: msg.logToken, feedback: value })
          .catch(() => {});
      }
    },

    // บันทึกคำถาม (ปิดบังเลขบัตร/อีเมล/เบอร์โทรแล้ว) — ใช้ดูว่าผู้ใช้ถามอะไรบ่อยและบอทตอบไม่ได้เรื่องไหน
    log(question, intentId, confidence, score = 0, msg = null) {
      this.$http
        .post("chatbot_log.insert.php", {
          action: "ask",
          question: maskPersonalData(question),
          intent_id: intentId,
          confidence,
          score: Math.round(score * 100) / 100,
          role: this.role,
          page: this.$route.path.slice(0, 100)
        })
        .then(res => {
          if (msg && res.data && res.data.status) {
            this.$set(msg, "logId", res.data.id);
            this.$set(msg, "logToken", res.data.token);
          }
        })
        .catch(() => {});
    },

    richText(text) {
      return String(text || "")
        .split(/\*\*(.+?)\*\*/g)
        .map((part, i) => ({ text: part, bold: i % 2 === 1 }))
        .filter(seg => seg.text);
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.body;
        if (el) el.scrollTop = el.scrollHeight;
      });
    }
  }
};
</script>

<style scoped>
.hrvec-chatbot {
  --hcb-primary: #1b365d;
  --hcb-primary-dark: #10243f;
  --hcb-bg: #f5f7fb;
  --hcb-border: #e3e8ef;
  --hcb-text: #263238;
  --hcb-muted: #607d8b;
}

/* ---------- ปุ่มเปิด ---------- */
.hcb-fab {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 200;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--hcb-primary) 0%, var(--hcb-primary-dark) 100%);
  box-shadow: 0 6px 18px rgba(16, 36, 63, 0.32);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.hcb-fab:hover,
.hcb-fab:focus-visible {
  transform: translateY(-2px) scale(1.04);
  box-shadow: 0 10px 24px rgba(16, 36, 63, 0.42);
  outline: none;
}
.hcb-fab-dot {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #b3151b;
  border: 2px solid #fff;
  animation: hcb-pulse 1.6s infinite;
}
.hcb-fab-hint {
  position: absolute;
  right: 72px;
  white-space: nowrap;
  background: #fff;
  color: var(--hcb-text);
  font-size: 0.85rem;
  padding: 8px 14px;
  border-radius: 18px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.15);
}

/* ---------- หน้าต่างแชท ---------- */
.hcb-panel {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 201;
  width: 390px;
  height: min(640px, calc(100vh - 48px));
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 18px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.22);
}
.hcb-header {
  display: flex;
  align-items: center;
  padding: 12px 12px 12px 16px;
  color: #fff;
  background: linear-gradient(135deg, var(--hcb-primary) 0%, var(--hcb-primary-dark) 100%);
}
.hcb-title {
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.2;
}
.hcb-subtitle {
  font-size: 0.75rem;
  opacity: 0.85;
}
.hcb-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 14px 8px;
  background: var(--hcb-bg);
}
.hcb-row {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
}
.hcb-row.is-user {
  align-items: flex-end;
}
.hcb-row.is-bot {
  align-items: flex-start;
}
.hcb-bubble {
  max-width: 88%;
  padding: 10px 14px;
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--hcb-text);
  word-break: break-word;
}
.hcb-bubble-user {
  color: #fff;
  background: var(--hcb-primary);
  border-radius: 16px 16px 4px 16px;
}
.hcb-bubble-bot {
  background: #fff;
  border: 1px solid var(--hcb-border);
  border-radius: 16px 16px 16px 4px;
}
.hcb-answer-title {
  font-weight: 600;
  color: var(--hcb-primary-dark);
  margin-bottom: 4px;
}
.hcb-text {
  margin: 0;
  white-space: pre-line;
}
.hcb-steps {
  margin: 8px 0 0;
  padding-left: 20px;
}
.hcb-steps li {
  margin-bottom: 4px;
}
.hcb-tip {
  display: flex;
  align-items: flex-start;
  margin-top: 10px;
  padding: 8px 10px;
  font-size: 0.82rem;
  background: #fff8e1;
  border-radius: 8px;
}
.hcb-role-note {
  margin-top: 8px;
  font-size: 0.8rem;
  color: var(--hcb-muted);
}
.hcb-links {
  margin-top: 10px;
}
.hcb-feedback {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: 8px;
  padding-top: 6px;
  font-size: 0.75rem;
  color: var(--hcb-muted);
  border-top: 1px dashed var(--hcb-border);
}
.hcb-feedback > span:first-child {
  margin-right: 4px;
}
.hcb-chips {
  max-width: 100%;
  margin-top: 8px;
}
.hcb-chips-label {
  display: block;
  margin-bottom: 4px;
  font-size: 0.75rem;
  color: var(--hcb-muted);
}
.hcb-categories {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  width: 100%;
  margin-top: 10px;
}
.hcb-category {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 8px 4px;
  font-size: 0.75rem;
  color: var(--hcb-text);
  background: #fff;
  border: 1px solid var(--hcb-border);
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}
.hcb-category:hover,
.hcb-category:focus-visible {
  background: #e3f2fd;
  border-color: var(--hcb-primary);
  outline: none;
}
.hcb-typing span {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin: 0 2px;
  border-radius: 50%;
  background: #90a4ae;
  animation: hcb-bounce 1.2s infinite ease-in-out;
}
.hcb-typing span:nth-child(2) {
  animation-delay: 0.15s;
}
.hcb-typing span:nth-child(3) {
  animation-delay: 0.3s;
}
.hcb-input {
  display: flex;
  align-items: center;
  padding: 8px 8px 8px 16px;
  border-top: 1px solid var(--hcb-border);
  background: #fff;
}
.hcb-input input {
  flex: 1;
  min-width: 0;
  height: 40px;
  font-size: 0.92rem;
  color: var(--hcb-text);
  border: none;
  outline: none;
  background: transparent;
}
.hcb-footnote {
  padding: 0 12px 8px;
  font-size: 0.68rem;
  text-align: center;
  color: var(--hcb-muted);
  background: #fff;
}

/* ---------- มือถือ: เต็มจอ ---------- */
@media (max-width: 599px) {
  .hcb-panel {
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100%;
    border-radius: 0;
  }
  .hcb-fab {
    right: 16px;
    bottom: 16px;
  }
  .hcb-fab-hint {
    display: none;
  }
}

@media print {
  .hrvec-chatbot {
    display: none !important;
  }
}

/* ---------- animation ---------- */
.hcb-fade-enter-active,
.hcb-fade-leave-active {
  transition: opacity 0.2s ease;
}
.hcb-fade-enter,
.hcb-fade-leave-to {
  opacity: 0;
}
.hcb-slide-enter-active,
.hcb-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.hcb-slide-enter,
.hcb-slide-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.98);
}
@keyframes hcb-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(179, 21, 27, 0.55);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(179, 21, 27, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(179, 21, 27, 0);
  }
}
@keyframes hcb-bounce {
  0%,
  80%,
  100% {
    transform: scale(0.6);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}
@media (prefers-reduced-motion: reduce) {
  .hcb-fab,
  .hcb-fab-dot,
  .hcb-typing span {
    animation: none;
    transition: none;
  }
}
</style>
